import React, { useState, useEffect, useRef, useCallback, ReactNode, useReducer, useMemo, useContext } from "react";
import WebConfig from "middlewares/WebConfig";
import type * as types from "./PhoenixFormsV4";
import type * as appdata from "./PhoenixFormsV4.AppData";
import type * as controls from "./PhoenixFormsV4.Controls";
import { getAllForms, getFormPreview, getSharedFormPreview } from "requests/phoenixForms";
import { PhoenixCaseForm, PhoenixRctCaseForm } from "interfaces/Mueller.Business.Models";
import { WebApiRequest, HTTP_POST } from "tools/webservice";
import { addSaveEvent } from "requests/caseEvent";

declare namespace PNX4 {
  class RuntimeEngine {
      constructor(data: object, interface: types.PhoenixFormsInterface);
      start(): void;
  }
}

export const PhoenixFormsContext = React.createContext<PhoenixFormsProviderValue | undefined>(undefined);

export const usePhoenixForms = (): PhoenixFormsProviderValue => {
  const {
    isLoaded,
    didFailLoading, 
    isSaving, 
    needsSave, 
    validationErrors, 
    ruleResults,
    initializePhoenix,
    getFormData, 
    reload,
    save,
    getProxy, 
    addControlRef, 
    removeControlRef, 
    setControlValue, 
    getWebRuntimeAppData,
    subscribeToOnSaveSuccess,
    jumpToControl,
    isPreview,
  } = useContext(PhoenixFormsContext);

  return useMemo(() => {
    return {
      isLoaded,
      didFailLoading, 
      isSaving, 
      needsSave, 
      validationErrors,
      ruleResults,
      initializePhoenix,
      getFormData, 
      reload,
      save,
      getProxy, 
      addControlRef, 
      removeControlRef, 
      setControlValue, 
      getWebRuntimeAppData,
      subscribeToOnSaveSuccess,
      jumpToControl,
      isPreview,
    }
  }, [
    isLoaded,
    didFailLoading, 
    isSaving, 
    needsSave, 
    validationErrors, 
    ruleResults,
    initializePhoenix,
    getFormData,
    reload, 
    save,
    getProxy, 
    addControlRef, 
    removeControlRef, 
    setControlValue, 
    getWebRuntimeAppData,
    subscribeToOnSaveSuccess,
    jumpToControl,
    isPreview,
  ]);
};

/** Provides theme context to a class component. Do not use this in functional components because they can just use the context */
export const withPhoenixForms = (WrappedComponent: React.ComponentType) => {
  return (props) => {
    return <WrappedComponent {...props} phoenixFormsContext={usePhoenixForms()} />;
  };
};

interface ControlRef {
  type: string;
  parentId: number;
  setControlState: React.Dispatch<React.SetStateAction<controls.Control>>; // only use updater function with this or you're going to have a bad time
  jumpToControl: () => void;
}

export interface PhoenixFormsProviderValue {
  isLoaded: boolean;
  didFailLoading: boolean;
  isSaving: boolean;
  isPreview: boolean;
  needsSave: boolean;
  validationErrors?: types.ValidationError[];
  ruleResults?: types.RuleResult[];
  initializePhoenix: (controlIdnumber: number) => void;
  getFormData: (form: PhoenixCaseFormOrPreview, forceReload?: boolean) => types.PhoenixForm;  
  reload: () => void;
  save: () => void;
  getProxy: () => types.PhoenixFormsProxy;
  addControlRef: (id: number, controlRef: ControlRef) => void;
  removeControlRef: (id: number) => void;
  setControlValue: (id: number, value: string) => void;
  getWebRuntimeAppData: () => appdata.WebRuntimeAppData;
  /** Adds an onSave success callback -- this returns its own cleanup function that needs to be called when the caller unmounts - see usePhoenixFormsSaveComplete hook for an easier way to use */
  subscribeToOnSaveSuccess: (callback: () => void) => () => void;
  jumpToControl: (id: number) => void;
};

export interface PhoenixCaseFormOrPreview extends PhoenixCaseForm {
  previewFormId: string;
  isAnonymousPreview: boolean;
}

interface ControlState {
  [controlId: number]: controls.Control;
}

type FormAction = 
  { type: "INITIALIZE_FORMS", forms: types.PhoenixForm[] }
  | { type: "CONTROL_PROPERTIES_CHANGED", changes: types.ControlPropertyChange[] }
  | { type: "CONTROL_ADDED", control: controls.Control }
  | { type: "CONTROL_DESTROY", id: number }

const formReducer = (state: ControlState, action: FormAction) => {
  switch (action.type) {
    case "INITIALIZE_FORMS": {
      const controlReduce = (acc, val) => {
        acc[val.id] = val;
        if (val.hasOwnProperty("controls")) {
          val["controls"].reduce(controlReduce, acc);
        }
        return acc;
      };
      return action.forms
        .flatMap(f => f.sections)
        .flatMap(s => s.controls)
        .reduce(controlReduce, {});
    }
    case "CONTROL_PROPERTIES_CHANGED": {
      const nextState = { ...state };
      for (const change of action.changes) {
        const control = nextState[change.id];
        for (const prop in change) {
          control[prop] = change[prop];
        }
      }
      return nextState;
    }
    case "CONTROL_ADDED": {
      const nextState = { ...state };      
      const repeat = nextState[action.control.parentId] as controls.RepeatControl;
      const repeatObject = action.control as controls.RepeatObject;
      repeat.controls.push(repeatObject); // TODO: this part should be handled in the runtime, but needs to be coordinated with the other library

      const add = (newControl) => {
        nextState[newControl.id] = newControl;

        if (newControl.hasOwnProperty("controls")) {
          for (const cc of newControl["controls"]) {
            add(cc);
          }
        }
      }
      add(repeatObject);
      return nextState;
    }
    case "CONTROL_DESTROY": {
      const nextState = { ...state };

      // TODO: vvvvvvvvv this part should be handled in the runtime, but needs to be coordinated with the other library
      const control = nextState[action.id];
      const parent = nextState[control.parentId] as any;
      const myIndex = parent.controls.indexOf(control);
      parent.controls.splice(myIndex, 1);
      // ^^^^^^^^^^^^^^^

      // TODO: the runtime should also be refactored so that this runs as a batch operation

      delete nextState[control.id];

      return nextState;
    }
    default:
      return state;
  }
};

interface Props {
  children: ReactNode;
};

export const PhoenixFormsProvider = (
  {
    children
  }: Props
) => {
  console.log("PhoenixFormsProvider Render");

  const mounted = useRef<boolean>(true);
  const formScriptLoaded = useRef<boolean>(false);
  
  const [currentControlIdNumber, setCurrentControlIdNumber] = useState<number>(null);
  const [currentFormPreviewId, setCurrentFormPreviewId] = useState<string>(null);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [didFailLoading, setDidFailLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [needsSave, setNeedsSave] = useState<boolean>(false);  
  const [validationErrors, setValidationErrors] = useState<types.ValidationError[]>(null);
  const [ruleResults, setRuleResults] = useState<types.RuleResult[]>(null);

  const [retryAttempt, setRetryAttempt] = useState<number>(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 1000;
  const RETRY_BACKOFF_FACTOR = 2;

  const pendingValidationErrors = useRef<types.ValidationError[]>(null);
  const pendingRuleResults = useRef<types.RuleResult[]>(null);

  const controlRefs = useRef<Record<number, ControlRef>>({});
  const rctNoCalcFormRefs = useRef<Record<string, PhoenixRctCaseForm>>({});

  const pnxProxy = useRef<types.PhoenixFormsProxy>(null);
  const pnxStructure = useRef<types.PhoenixForm[]>(null);
  const pnxSettings = useRef<types.FormSettings>(null);
  const pnxAppData = useRef<appdata.AppData>(null);
  const pnxWebRuntimeAppdata = useRef<appdata.WebRuntimeAppData>(null);

  const onSaveSubscribers = useRef<Set<() => void>>(new Set());

  const [state, dispatch] = useReducer<React.Reducer<ControlState, FormAction>>(formReducer, {});

  useEffect(() => {
    const script = document.createElement("script");
    script.src = `${WebConfig.phoenixUrl}/webforms4/phoenix.forms.core.js`;
    script.onload = () => {
       formScriptLoaded.current = true;
    };
    document.body.appendChild(script);

    const formsTipCss = document.createElement("link");
    formsTipCss.rel = "stylesheet";
    formsTipCss.href = `${WebConfig.phoenixUrl}/services/cdn/webforms4/topic-content.css`;
    document.head.appendChild(formsTipCss);
  }, []);

  useEffect(() => {
    // error handler when loadCase fails (can't retry in the error callback because of dupe request tracking)
    if (retryAttempt > 0 && retryAttempt <= MAX_RETRIES) {
      const delay = RETRY_DELAY * Math.pow(RETRY_BACKOFF_FACTOR, retryAttempt - 1);
      const timer = setTimeout(() => {
        loadCaseRetry();
      }, delay);
    
      return () => clearTimeout(timer);
    }
  }, [retryAttempt]);

  // Callbacks invoked by phoenix forms runtime.
  const pnx: types.PhoenixFormsInterface = {
    ready: (proxy: types.PhoenixFormsProxy, structure: types.PhoenixForm[], settings: types.FormSettings, appData: appdata.AppData): void => {
      pnxProxy.current = proxy;
      pnxStructure.current = structure;
      pnxSettings.current = settings;
      pnxAppData.current = appData;
      pnxWebRuntimeAppdata.current = appData.json["web_runtime"];

      dispatch({ type: "INITIALIZE_FORMS", forms: structure });
      setIsLoaded(true);
    },
    onBeforeSave: (): void => {
      setIsSaving(true);
    },
    onSave: (data: string, photoData: types.PhotoSaveData): void => {

      const saveObj = {
        data: data,
        saveKey: pnxWebRuntimeAppdata.current.saveKey,
        revision: pnxWebRuntimeAppdata.current.fileRevision,
        photoLinks: photoData.links,
        photoMetadatas: photoData.metadata
      };
      
      const url = `${pnxWebRuntimeAppdata.current.host}/services/forms4/${pnxWebRuntimeAppdata.current.fileId}/save-data/${pnxWebRuntimeAppdata.current.sessionKey}`;
      
      const xmlhttp = new XMLHttpRequest();
      xmlhttp.open("POST", url, true);
      xmlhttp.setRequestHeader("Content-Type", "application/json");
      
      let complete = false;
      xmlhttp.onreadystatechange = () => {
          if (xmlhttp.readyState === 4 && !complete) {
              complete = true;
      
              if (xmlhttp.status !== 0) {
                  if (xmlhttp.status === 200) {
                      const response = JSON.parse(xmlhttp.responseText);
                      pnxWebRuntimeAppdata.current.saveKey = response.saveKey;
                      onPhoenixServicesSaveSuccess();
                  }
                  else {
                    onFinishedSavingError();
                  }
              }              
          }
      };
      
      xmlhttp.send(JSON.stringify(saveObj));
    },
    onBeforeValidate: (): void => {
      // maybe nothing, classic library makes sure change event is fired on currently focused control
    },
    onValidate: (errors: types.ValidationError[]): void => {
      pendingValidationErrors.current = errors;
      pnxProxy.current.rules();
    },
    onRules: (results: types.RuleResult[]): void => {
      pendingRuleResults.current = results;
      pnxProxy.current.save();
    },
    onControlPropertiesChanged: (batch: types.ControlPropertyChange[]): void => {      
      // hacking around awkward interaction between react and forms library
      //  directly updating state on rendered controls, then dispatching action to update reducer state
      batch.forEach(change => {
        const cRef = controlRefs.current[change.id];

        if (!cRef) return; // the control is not rendered (might be on another form)

        cRef.setControlState(cs => {
          const nextState = {...cs};
          for (const prop in change) {
            nextState[prop] = change[prop];
          }
          return nextState;
        });
      });

      dispatch({type: "CONTROL_PROPERTIES_CHANGED", changes: batch});
      setNeedsSave(true);
    },
    onPhotoControlUpdated: (id: number): void => {
      const cRef = controlRefs.current[id];

      if (!cRef) return; // the control is not rendered, this probably should never happen unless you messed something up while developing

      // force re-render of control
      cRef.setControlState(cs => ({...cs}));
      setNeedsSave(true);

      // automatically save after photo control is updated so that this is synced with the PhotoTool control
      setIsSaving(true);
      pnxProxy.current.save();
    },
    onControlAdded: (props: controls.Control): void => {
      // hacking around awkward interaction between react and forms library
      //  dispatch action to reducer, then forcing a re-render of the repeat. the repeat will get its controls from reducer state.
      dispatch({type: "CONTROL_ADDED", control: props});
      const repeatRef = controlRefs.current[props.parentId];
      repeatRef.setControlState(cs => ({...cs}));
      setNeedsSave(true);
    },
    onControlDestroy: (id: number): void => {
      // hacking around awkward interaction between react and forms library
      //  dispatch action to reducer, then when we've confirmed that the repeat object is destroyed, force re-render of the repeat.
      dispatch({type: "CONTROL_DESTROY", id: id});
      const cRef = controlRefs.current[id];

      if (!cRef) return; // the control is not rendered, this probably should never happen unless you messed something up while developing

      if (cRef.type === "repeatobject") {
        const repeatRef = controlRefs.current[cRef.parentId];
        repeatRef.setControlState(cs => ({...cs}));
      }
      setNeedsSave(true);
    },
  };

  const onFinishedSavingError = () => {
    setIsSaving(false);
    // TODO: error handling
  }

  const onFinishedSavingSuccess = () => {
    setNeedsSave(false);
    setIsSaving(false);
    setValidationErrors(pendingValidationErrors.current);
    setRuleResults(pendingRuleResults.current);
    if (currentControlIdNumber) {
      addSaveEvent(currentControlIdNumber);
    }

    onSaveSubscribers.current.forEach(cb => {
      try {
        cb();
      }
      catch(e) {
        console.error('Error in save completion callback:', e);
      }
    });
  }

  const onPhoenixServicesSaveSuccess = () => {
    const rctBackfills = Object.values(rctNoCalcFormRefs.current);
    let rctBackfillCount = rctBackfills.length;
    let rctBackfillErrors = 0;
    if (rctBackfillCount) { // call backfill rct
      const tryFinish = () => {
        rctBackfillCount--;
        if (!rctBackfillCount) {
          if (!rctBackfillErrors)
            onFinishedSavingSuccess();
          else
            onFinishedSavingError();
        }
      };

      for (const rct of rctBackfills) {
        WebApiRequest({
          endpoint: rct.backfillUrl,
          method: HTTP_POST,
          successCallback: () => {
            tryFinish();
          },
          errorCallback: () => {
            rctBackfillErrors++;
            tryFinish();
          },
          mountedRef: mounted,
        });
      }
    }
    else {
      onFinishedSavingSuccess();
    }
  };

  const loadCaseSuccess = (data: any) => {
    const runtime = new PNX4.RuntimeEngine(data, pnx); // don't keep a reference to the runtime, only interact with it via the proxy object (returned in the "ready" callback)
    runtime.start();
  };

  const loadCaseRetry = () => { // phoenix token doesn't expire with the same cadence of the mueller sso, this ensures we get a refreshed token
    getAllForms(currentControlIdNumber,
      (data) => {
        loadCaseSuccess(data);
      },
      () => { 
        if (retryAttempt < MAX_RETRIES) {
          setRetryAttempt(retry => retry + 1);
        }
        else { // we failed out
          setDidFailLoading(true);
        }
      },
      mounted
    );
  };

  const clearOldFormData = () => {
    controlRefs.current = {};
    rctNoCalcFormRefs.current = {};
    setValidationErrors(null);
    setIsLoaded(false);
    setDidFailLoading(false);
    setCurrentControlIdNumber(null);
    setCurrentFormPreviewId(null);
  };

  const loadCase = (controlIdnumber: number) => {
    clearOldFormData();
    setCurrentControlIdNumber(controlIdnumber);
    setRetryAttempt(0);
    
    getAllForms(controlIdnumber, 
      loadCaseSuccess,
      () => {
        setRetryAttempt(1); // we need to call getAllForms again, but we can't do it here without having the duplicate request canceled. setting this to trigger an effect
      },
      mounted
    );
  };

  const loadFormPreview = (phoenixFormId: string, isAnonymousPreview: boolean) => {
    clearOldFormData();
    setCurrentFormPreviewId(phoenixFormId);

    const loadPreviewFunc = isAnonymousPreview ? getSharedFormPreview : getFormPreview;

    loadPreviewFunc(phoenixFormId, formData => {
      loadCaseSuccess(formData);
    }, mounted);
  };

  const initializePhoenix = useCallback((controlIdnumber: number) => {
    if (controlIdnumber === currentControlIdNumber) return;

    loadCase(controlIdnumber);
  }, [isLoaded, currentControlIdNumber]);

  const getFormData = useCallback((form: PhoenixCaseFormOrPreview, forceReload = false) => {
    const isPreview = !!form.previewFormId;
    if (isPreview) {
      if (form.previewFormId !== currentFormPreviewId || forceReload) {
        loadFormPreview(form.previewFormId, form.isAnonymousPreview);
        return null;
      }
    } else if (form.controlNumber !== currentControlIdNumber || forceReload) {
      loadCase(form.controlNumber);
      return null;
    }

    if (!isLoaded) {
      return null;
    }

    let formId = form.previewFormId ?? form.formId;
    if (form.rct?.isNoCalc) rctNoCalcFormRefs.current[formId] = form.rct; // track NoCalc RCT forms so we can run backfill on save    
    return pnxStructure.current.filter(f => f.id === formId)[0];
  }, [isLoaded, currentControlIdNumber]);

  const reload = useCallback(() => {
    if (currentControlIdNumber)
      loadCase(currentControlIdNumber);
  }, [isLoaded, currentControlIdNumber]);

  const save = useCallback(() => {
    if(isSaving || !isLoaded) return;
    setIsSaving(true);
    pnxProxy.current.validate();
  }, [isSaving, isLoaded]);

  const getProxy = useCallback(() => {
    return pnxProxy.current;
  }, []);

  const addControlRef = useCallback((id: number, cRef: ControlRef) => {
    controlRefs.current[id] = cRef;
  }, []);

  const removeControlRef = useCallback((id: number) => {
    delete controlRefs.current[id];
  }, []);
  
  const setControlValue = useCallback((id: number, value: string) => {
    // hacking around awkward interaction between react and forms library
    //  wrapped proxy setControlValue to ensure reducer state control value is also updated
    //  OG library had no concept of controlled components so we mimic the state update here
    pnx.onControlPropertiesChanged([{id, value}]);
    getProxy().setControlValue(id, value);    
  }, []);

  const getWebRuntimeAppData = useCallback(() => {
    return pnxWebRuntimeAppdata.current;
  }, []);

  const subscribeToOnSaveSuccess = useCallback((callback: () => void) => {

    onSaveSubscribers.current.add(callback);
    
    // Return cleanup function
    return () => {
      if (onSaveSubscribers.current.has(callback)) {
        onSaveSubscribers.current.delete(callback);
      }
    };
  }, []);

  const jumpToControl = useCallback((id: number) => {
    const jumpControl = controlRefs.current[id];
    if (!jumpControl) return;

    jumpControl.jumpToControl();
  }, [isLoaded, currentControlIdNumber]);

  const contextValue: PhoenixFormsProviderValue = useMemo(() => {
    return {
      isLoaded,
      didFailLoading, 
      isSaving, 
      needsSave, 
      validationErrors,
      initializePhoenix, 
      getFormData, 
      reload,
      save,
      getProxy, 
      addControlRef, 
      removeControlRef, 
      setControlValue, 
      getWebRuntimeAppData,
      subscribeToOnSaveSuccess,
      jumpToControl,
      isPreview: !!currentFormPreviewId,
    }
  }, [
    isLoaded, 
    didFailLoading,
    isSaving, 
    needsSave, 
    validationErrors,
    initializePhoenix, 
    getFormData, 
    reload,
    save,
    getProxy, 
    addControlRef, 
    removeControlRef, 
    setControlValue, 
    getWebRuntimeAppData,
    subscribeToOnSaveSuccess,
    jumpToControl,
    currentFormPreviewId,
  ]);

  return (
    <PhoenixFormsContext.Provider value={contextValue}>
      {children}
    </PhoenixFormsContext.Provider>
  )
};